import { Button, FormInput, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { z } from "zod";
import type {
  VehicleMakeCreateInput,
  VehicleMakeUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";

const VehicleMakeFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຍີ່ຫໍ້"),
  logoKey: z.string().optional(),
});

type VehicleMakeFormValues = z.infer<typeof VehicleMakeFormSchema>;

type VehicleMakeFormProps = {
  initialValues?: Partial<VehicleMakeFormValues>;
  onSubmit: (values: VehicleMakeCreateInput | VehicleMakeUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  submitLabel?: string;
};

export function VehicleMakeForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  submitLabel = "ບັນທຶກ",
}: VehicleMakeFormProps) {
  const methods = RHF.useForm<VehicleMakeFormValues>({
    resolver: zodResolver(VehicleMakeFormSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      logoKey: initialValues?.logoKey ?? "",
    },
  });

  const handleSubmit = (vals: VehicleMakeFormValues) => {
    onSubmit({
      name: vals.name,
      logoKey: vals.logoKey?.trim() ? vals.logoKey.trim() : null,
    });
  };

  return (
    <FormRoot<VehicleMakeFormValues>
      methods={methods}
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <FormInput name="name" label="ຊື່ຍີ່ຫໍ້ລົດ" placeholder="ຕົວຢ່າງ: Toyota" />
      <RHF.Controller
        name="logoKey"
        control={methods.control}
        render={({ field }) => (
          <ImageKeyUploadField
            value={field.value ?? ""}
            onChange={field.onChange}
            keyPrefix="uploads/catalog/vehicle-makes"
            label="ຮູບໂລໂກ້ຍີ່ຫໍ້"
            aspectHint="ແນະນຳ 1:1"
            aspectRatio="aspect-square"
          />
        )}
      />
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
      </div>
    </FormRoot>
  );
}
